import { useState } from 'react';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import {
  Dialog,
  DialogBody,
  type DialogProps,
  DialogTitle,
} from '@/components/ui/dialog';
import { getFileType } from '@/utils/getFileType';

import { DocumentActions } from './document-actions';
import { DocumentPreviewImage } from './document-preview-image';
import { DocumentPreviewPdf } from './document-preview-pdf';
import { ScaleController } from './scale-controller';

export type DocumentPreviewDialogProps = {
  file?: File;
  document?: { name: string; extension: string };
  onFileNotSupported?: (file: File) => void;
} & Pick<DialogProps, 'open' | 'onClose'>;

export function DocumentPreviewDialog({
  file,
  document,
  onFileNotSupported,
  ...props
}: DocumentPreviewDialogProps) {
  const [scale, setScale] = useState(1);
  const fileType = getFileType(document?.extension);

  if (!file || !document) {
    return null;
  }

  if (!fileType) {
    onFileNotSupported?.(file);
    return null;
  }

  return (
    <Dialog {...props} size="5xl">
      <DialogTitle className="flex items-baseline justify-between gap-8">
        <span className="text-ellipsis">{document.name}</span>
        <ScaleController value={scale} onChange={setScale} />
        <DocumentActions file={file} />
      </DialogTitle>
      <DialogBody className="flex h-[80vh] w-full justify-center overflow-auto">
        {fileType === 'pdf' ? (
          <DocumentPreviewPdf file={file} scale={scale} />
        ) : null}
        {fileType === 'image' ? (
          <DocumentPreviewImage file={file} alt={document.name} scale={scale} />
        ) : null}
      </DialogBody>
    </Dialog>
  );
}
